import type { Vault } from '@/types/vault'
import { calculateTokenPriceInUsd } from '@/utils/calculateTokenPriceInUsd'
import { getDefiSdk } from '@/utils/defiSdk'
import { useQueries, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { useMemo } from 'react'
import { type Address, formatUnits } from 'viem'

const queryKey = ['returns'] as const

interface ActionsArgs {
  address: Address | undefined
  vault: Pick<Vault, 'vaultId' | 'network' | 'childrenVaults'>
}

interface VaultReturnsResponse {
  returnsNative: string
  decimals: number
  assetPriceInUsd: string
}

export function useReturnsQuery({ address, vault }: ActionsArgs) {
  const { data: vaultData, isLoading: isVaultLoading } = useQuery({
    queryKey: [...queryKey, 'vault', vault.network.name, vault.vaultId, address] as const,
    queryFn: async () => {
      if (!address) return null
      const defiSdk = getDefiSdk()
      const response = await defiSdk.getUserVaultTotalReturns({
        path: {
          network: vault.network.name,
          vaultId: vault.vaultId,
          userAddress: address,
        },
      })
      return response
    },
    enabled: !!address,
    retry: 2,
    staleTime: 2 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
  })

  const childrenQueries = useQueries({
    queries: (vault.childrenVaults ?? []).map((child) => ({
      queryKey: [...queryKey, 'vault', vault.network.name, child.vaultId, address] as const,
      queryFn: async (): Promise<VaultReturnsResponse | null> => {
        if (!address) return null
        const defiSdk = getDefiSdk()
        const response = await defiSdk.getUserVaultTotalReturns({
          path: {
            network: vault.network.name,
            vaultId: child.vaultId,
            userAddress: address,
          },
        })
        return response as VaultReturnsResponse
      },
      enabled: !!address,
      retry: 2,
      staleTime: 2 * MINUTE_IN_MILLISECONDS,
      refetchOnWindowFocus: true,
    })),
  })

  const isLoading = isVaultLoading || childrenQueries.some((q) => q.isLoading)

  const data = useMemo(() => {
    if (!vaultData) return null

    const vaultReturnsNative = BigInt(vaultData.returnsNative)
    const vaultReturnsUsd = calculateTokenPriceInUsd(
      Number(formatUnits(vaultReturnsNative, vaultData.decimals)),
      Number(vaultData.assetPriceInUsd)
    )

    const childrenData = childrenQueries
      .map((q) => q.data)
      .filter((data): data is VaultReturnsResponse => data !== null && data !== undefined)

    const childrenReturnsNative = childrenData.reduce((acc, curr) => acc + BigInt(curr.returnsNative), 0n)

    const childrenReturnsUsd = childrenData.reduce(
      (acc, curr) =>
        acc +
        calculateTokenPriceInUsd(
          Number(formatUnits(BigInt(curr.returnsNative), curr.decimals)),
          Number(curr.assetPriceInUsd)
        ),
      0
    )

    return {
      returnsNative: vaultReturnsNative + childrenReturnsNative,
      returnsUsd: vaultReturnsUsd + childrenReturnsUsd,
    }
  }, [vaultData, childrenQueries])

  return {
    returnsNative: data?.returnsNative,
    returnsUsd: data?.returnsUsd,
    isLoading,
  }
}
